import { useEffect, useState, useCallback } from "react";
import Title from "@/components/molecules/Title";
import {
  Paper,
  Grid,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import { useSetting } from "@/context";
import { ContractFeeConstants, taxMessageOf } from "./ContractFeeConstants";
import { AutoCompleteSearch } from "@/components/molecules/AutoCompleteSearch";
import { CommonButton } from "@/components/common/buttons/CommonButton";
import { ChargeFeeTariffPeriod } from "@/models/chargeFeeTariffPeriod";
import { ChargeFeeMonthlySurchargesUnitPrice } from "@/models/chargeFeeMonthlySurchargesUnitPrice";
import { MonthlyBillingNotesOutput } from "@/models/monthlyBillingNotes";
import { Office, ONLY_CONTRACT } from "@/models/office";
import {
  getChargeFeeMonthlySurchargesUnitPriceAPI,
  getChargeFeeTariffPeriodAPI,
  getContractFeeMessageAPI,
} from "@/util/api/contractFee";
import { getOfficesAPI } from "@/util/api/API";
import { Picker } from "@/components/atoms/Picker";
import { TitleGroup } from "@/components/molecules/TitleGroup";
import Cookies from "js-cookie";
import { CommonConstants, SINGLE_OFFICE_ID } from "@/common/CommonConstants";
import { ContractFeeError } from "@/models/contractFee";
import { EmptyResultMessage } from "@/components/atoms/EmptyResultMessage";
import { getJSTCurrentTime } from "@/util/datetime";
import { getOfficeOptionLabel } from "@/util";

function formatMonth(yyyyMM?: string) {
  return yyyyMM ? `${yyyyMM.slice(0, 4)}/${yyyyMM.slice(-2)}` : "";
}

function formatYen(digits: number, yen?: number) {
  return yen != null
    ? `${yen.toLocaleString(undefined, {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      })} 円`
    : "";
}

function formatKW(kw?: number) {
  return kw != null ? `${kw} kWh` : "";
}

function ContractFeeContent(): JSX.Element {
  const setting = useSetting();

  const [offices, setOffices] = useState<Office[]>([]);

  const [officeSelected, setOfficeSelected] = useState<Office | null>(null);
  const [targetMonth, setTargetMonth] = useState(
    new Date(getJSTCurrentTime("date"))
      .toISOString()
      .slice(0, 7)
      .replace("-", "")
  );
  const [displayMonth, setDisplayMonth] = useState(
    new Date(getJSTCurrentTime("date"))
      .toISOString()
      .slice(0, 7)
      .replace("-", "")
  );
  const [error, setError] = useState<ContractFeeError>({});

  const [period, setPeriod] = useState<ChargeFeeTariffPeriod>();
  const [surcharge, setSurcharge] =
    useState<ChargeFeeMonthlySurchargesUnitPrice>();
  const [message, setMessage] = useState<MonthlyBillingNotesOutput>();

  const [searched, setSearched] = useState(false);

  const handleChangeOffice = useCallback(
    (e: React.SyntheticEvent<Element, Event>, value: Office | null) => {
      setError({ ...error, officeId: false });
      setOfficeSelected(value);
      Cookies.set(SINGLE_OFFICE_ID, value?.id.toString() ?? "");
    },
    [error]
  );

  const handleChangeTargetMonth = useCallback(
    (value?: string | undefined, inRange?: boolean | undefined) => {
      setError({ ...error, targetMonth: false });
      setTargetMonth(value ?? "");
    },
    [error]
  );

  const handleClickClear = useCallback(() => {
    setError({});
    setOfficeSelected(null);
    setTargetMonth(
      new Date(getJSTCurrentTime("date"))
        .toISOString()
        .slice(0, 7)
        .replace("-", "")
    );
  }, []);

  const search = useCallback((office: Office, targetMonth: string) => {
    setSearched(true);

    getChargeFeeTariffPeriodAPI(office.id, targetMonth)
      .then(({ data }) => setPeriod(data))
      .then(() => setDisplayMonth(targetMonth))
      .catch((e) =>
        console.log("===Error occurred while getting period===", e)
      );

    getChargeFeeMonthlySurchargesUnitPriceAPI(office.id, targetMonth)
      .then(({ data }) => setSurcharge(data))
      .catch((e) =>
        console.log("===Error occurred while getting surcharge===", e)
      );

    getContractFeeMessageAPI(targetMonth)
      .then(({ data }) => setMessage(data))
      .catch((e) =>
        console.log("===Error occurred while getting message===", e)
      );
  }, []);

  const handleClickeSearch = useCallback(() => {
    if (officeSelected == null || !targetMonth?.length) {
      setError({
        officeId: officeSelected == null,
        targetMonth: !targetMonth?.length,
      });
      return;
    }

    search(officeSelected, targetMonth);
  }, [officeSelected, search, targetMonth]);

  useEffect(() => {
    setting.updateTitleHeader(ContractFeeConstants.ContractFee);
    getOfficesAPI(ONLY_CONTRACT)
      .then(({ data }) => {
        const ofs = data ?? [];
        setOffices(ofs);

        const savedOffice =
          ofs.find(({ id }) => id === Number(Cookies.get(SINGLE_OFFICE_ID))) ??
          null;
        setOfficeSelected(savedOffice);
        if (savedOffice != null) {
          search(savedOffice, targetMonth);
        }
      })
      .catch((e) => console.log("Error occurred while getting offices===", e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Grid
        container
        spacing={2}
        sx={{ paddingLeft: 2, paddingRight: 2, mt: 3 }}
      >
        <Grid container spacing={2} sx={{ display: "flex", pl: 2 }}>
          <Title style={{ flexGrow: 1 }}>
            {ContractFeeConstants.ContractFee}
          </Title>
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ paddingLeft: 2, paddingRight: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={12} lg={12}>
            <Paper
              sx={{
                p: 2,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={6} md={6} lg={6.1} className="required">
                  <AutoCompleteSearch
                    options={offices}
                    autoHighlight
                    value={officeSelected}
                    onChange={handleChangeOffice}
                    getOptionLabel={getOfficeOptionLabel}
                    label={CommonConstants.CompanyOffice}
                    error={error.officeId}
                  />
                </Grid>
                <Grid
                  item
                  xs={2}
                  md={2}
                  lg={2.1}
                  sx={{ pt: "0!important" }}
                  className="required"
                >
                  <Picker
                    forSearch
                    type="month"
                    label={ContractFeeConstants.TargetMonth}
                    value={targetMonth}
                    parse={(input) => `${input.slice(0, 4)}-${input.slice(-2)}`}
                    decode={(out) => out.slice(0, 7).replace("-", "")}
                    onChange={handleChangeTargetMonth}
                    error={error.targetMonth}
                  />
                </Grid>
                <Grid
                  item
                  xs={4}
                  md={4}
                  lg={3.8}
                  container
                  justifyContent="flex-end"
                >
                  <CommonButton
                    btnType="Search"
                    onClickBtn={handleClickeSearch}
                  />
                  <CommonButton
                    onClickBtn={handleClickClear}
                    btnType="ResetSearchCriteria"
                    sx={{ ml: 2 }}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* データエリア */}
          <Grid item xs={10} md={10} lg={14}>
            <Paper sx={{ p: 1, display: "flex", flexDirection: "column" }}>
              {period?.id != null || surcharge?.id != null ? (
                <>
                  <Table
                    className="gx-info-table-no-width"
                    size="small"
                    sx={{ width: "30%" }}
                  >
                    <TableBody>
                      <TableRow>
                        <TableCell sx={{ fontSize: "1.3rem" }}>
                          <div style={{ paddingTop: 20 }}>
                            {ContractFeeConstants.Month}
                          </div>
                        </TableCell>
                        <TableCell align="right">
                          <div style={{ paddingTop: 20 }}>
                            {formatMonth(displayMonth)}
                          </div>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                  <Table size="small" sx={{ width: "70%", mt: "20px" }}>
                    <TableBody>
                      <TableRow>
                        <TableCell colSpan={3} className="body-none-custom">
                          {taxMessageOf(period)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                  <TitleGroup
                    style={{ marginLeft: 15 }}
                    text={ContractFeeConstants.ChargeServiceCostUnitPriceParKw}
                  />
                  <Table
                    className="gx-info-table-no-width"
                    size="small"
                    sx={{ width: "30%" }}
                  >
                    <TableBody>
                      {period?.isMinimumChargeSpecified && (
                        <TableRow>
                          <TableCell>
                            {ContractFeeConstants.MinChargeAmount}
                          </TableCell>
                          <TableCell align="right">
                            {formatKW(
                              period?.chargeFeeStepUnitPrices[0]
                                ?.targetMaxKwhPerMonth
                            )}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                  <Table
                    size="small"
                    sx={{
                      width: "50%",
                      mt: period?.isMinimumChargeSpecified ? 1 : undefined,
                    }}
                  >
                    <TableHead className="list-head">
                      <TableRow>
                        <TableCell colSpan={3}>
                          {ContractFeeConstants.AmountOfUsage}
                        </TableCell>
                        <TableCell align="right">
                          {ContractFeeConstants.UnitPrice}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {period?.chargeFeeStepUnitPrices?.length &&
                        period.chargeFeeStepUnitPrices.map((row) => (
                          <TableRow key={row.id}>
                            <TableCell align="right">
                              {formatKW(row.targetMinKwhPerMonth)}
                            </TableCell>
                            <TableCell sx={{ width: "20px", pl: 0, pr: 0 }}>
                              ～
                            </TableCell>
                            <TableCell>
                              {formatKW(row.targetMaxKwhPerMonth)}
                              {row.targetMaxKwhPerMonth != null ? "以下" : ""}
                            </TableCell>
                            <TableCell align="right">
                              {formatYen(2, row.yenPerKwh)}
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                  <Table size="small" sx={{ width: "70%" }}>
                    <TableBody>
                      <TableRow>
                        <TableCell colSpan={3} className="body-none-custom">
                          {ContractFeeConstants.Message1}
                          <br />
                          {ContractFeeConstants.Message2}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                  <TitleGroup
                    style={{
                      paddingTop: 30,
                      marginLeft: 15,
                    }}
                    text={ContractFeeConstants.AdjustCostUnitPriceParKw}
                  />
                  <Table
                    className="gx-info-table-no-width"
                    size="small"
                    sx={{ width: "30%" }}
                  >
                    <TableBody>
                      <TableRow>
                        <TableCell>
                          {formatMonth(surcharge?.targetMonth)}
                        </TableCell>
                        <TableCell align="right">
                          {formatYen(2, surcharge?.yenPerKwh)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                  <Table size="small" sx={{ width: "100%" }}>
                    <TableBody>
                      <TableRow>
                        <TableCell
                          colSpan={3}
                          className="body-none-custom"
                          sx={{ whiteSpace: "pre-wrap" }}
                        >
                          {message?.text}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </>
              ) : searched ? (
                <EmptyResultMessage />
              ) : (
                <></>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
}

export function ContractFee(): JSX.Element {
  return <ContractFeeContent />;
}
